import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, Observable, throwError, Subject } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { environment } from '@src/environments/environment';
import {
  MfaRequiredResponseDto,
  VerifyMfaDto,
  VerifyMfaResponseDto,
  MfaStatusDto,
  MfaEnableResponseDto,
  MfaDisableResponseDto,
} from '@app/modules/auth/models';
import { MfaSessionEntity, UserEntity } from '@app/modules/auth/models';
import { DeviceService } from '@app/shared/services/device.service';

/**
 * MfaService (OPCJONALNY)
 * Odpowiedzialny za:
 * - MFA flow (email)
 * - MFA enable/disable
 * - MFA status
 * - MFA session management
 * 
 * Można usunąć cały plik jeśli projekt nie potrzebuje MFA.
 */
@Injectable({
  providedIn: 'root',
})
export class MfaService {
  // Obserwowalne stany
  public mfaRequired$ = new BehaviorSubject<boolean>(false);
  private _mfaSession$ = new BehaviorSubject<MfaSessionEntity | null>(null);
  public mfaSession$ = this._mfaSession$.asObservable();

  // Subject dla zmian statusu MFA
  private mfaStatusChanged$ = new Subject<boolean>();
  public mfaStatusChanged = this.mfaStatusChanged$.asObservable();

  constructor(
    private http: HttpClient,
    private deviceService: DeviceService
  ) {
    this.initializeMfaSession();
  }

  /**
   * Pobierz aktualną sesję MFA
   */
  getMfaSession(): MfaSessionEntity | null {
    return this._mfaSession$.value;
  }

  /**
   * Czy wymagane jest MFA
   */
  isMfaRequired(): boolean {
    return this.mfaRequired$.value;
  }

  /**
   * Ustaw MFA sesję
   */
  setMfaSession(response: MfaRequiredResponseDto): void {
    const mfaSession = new MfaSessionEntity(
      response.tempToken,
      response.userId || 0, // Backend nie zawsze wysyła userId
      response.mfaMethod || 'email', // Domyślnie email
      300, // 5 minut
      3 // 3 próby
    );
    this._mfaSession$.next(mfaSession);
    localStorage.setItem('mfaSession', JSON.stringify(mfaSession));
    this.mfaRequired$.next(true);
  }

  /**
   * Wyczyść MFA sesję
   */
  clearMfaSession(): void {
    this._mfaSession$.next(null);
    localStorage.removeItem('mfaSession');
    this.mfaRequired$.next(false);
  }

  /**
   * Weryfikacja MFA kodu
   */
  verifyMfa(dto: VerifyMfaDto): Observable<VerifyMfaResponseDto> {
    // Dodaj deviceId do requestu
    const requestBody = {
      ...dto,
      deviceId: this.deviceService.getDeviceId()
    };
    
    return this.http
      .post<VerifyMfaResponseDto>(
        `${environment.apiUrl}auth/verify-mfa`,
        requestBody,
        { withCredentials: true }  // Ważne dla cookies!
      )
      .pipe(
        tap((response: VerifyMfaResponseDto) => {
          this.clearMfaSession();
        }),
        catchError((error) => {
          // Zmniejsz próby w MFA session
          const mfaSession = this._mfaSession$.value;
          if (mfaSession) {
            mfaSession.decrementAttempts();
            if (!mfaSession.hasAttemptsLeft) {
              this.clearMfaSession();
              return throwError(() => new Error('Zbyt wiele nieudanych prób. Zaloguj się ponownie.'));
            }
          }
          return throwError(() => error);
        })
      );
  }

  /**
   * Pobierz status MFA użytkownika
   * GET /api/v1/auth/mfa/status
   */
  getMfaStatus(): Observable<MfaStatusDto> {
    return this.http
      .get<MfaStatusDto>(`${environment.apiUrl}auth/mfa/status`)
      .pipe(
        tap((response: MfaStatusDto) => {
          this.mfaStatusChanged$.next(response.mfaEnabled);
        }),
        catchError((error) => throwError(() => error))
      );
  }

  /**
   * Włącz MFA dla użytkownika
   * POST /api/v1/auth/mfa/enable
   */
  enableMfa(): Observable<MfaEnableResponseDto> {
    return this.http
      .post<MfaEnableResponseDto>(`${environment.apiUrl}auth/mfa/enable`, {})
      .pipe(
        tap((response: MfaEnableResponseDto) => {
          this.mfaStatusChanged$.next(true);
        }),
        catchError((error) => throwError(() => error))
      );
  }

  /**
   * Wyłącz MFA dla użytkownika
   * POST /api/v1/auth/mfa/disable
   */
  disableMfa(): Observable<MfaDisableResponseDto> {
    return this.http
      .post<MfaDisableResponseDto>(`${environment.apiUrl}auth/mfa/disable`, {})
      .pipe(
        tap((response: MfaDisableResponseDto) => {
          this.mfaStatusChanged$.next(false);
        }),
        catchError((error) => throwError(() => error))
      );
  }

  /**
   * Obsłuż błąd 403 (MFA required)
   */
  handleMfaError(error: HttpErrorResponse): boolean {
    if (error.status === 403 && error.error.tempToken) {
      const mfaError = error.error as MfaRequiredResponseDto;
      this.setMfaSession(mfaError);
      return true;
    }
    return false;
  }

  /**
   * Ponownie wyślij kod MFA
   */
  resendMfaCode(): Observable<MfaRequiredResponseDto> {
    const session = this._mfaSession$.value;
    if (!session) {
      return throwError(() => new Error('Brak aktywnej sesji MFA'));
    }

    return this.http
      .post<MfaRequiredResponseDto>(
        `${environment.apiUrl}auth/resend-mfa`,
        { tempToken: session.tempToken }
      )
      .pipe(
        tap((response: MfaRequiredResponseDto) => {
          // Ustaw nową sesję z nowym tokenem
          this.setMfaSession(response);
        }),
        catchError((error) => throwError(() => error))
      );
  }

  /**
   * Inicjalizuj MFA sesję przy starcie aplikacji
   */
  private initializeMfaSession(): void {
    const mfaSessionJson = localStorage.getItem('mfaSession');
    if (mfaSessionJson) {
      try {
        const data = JSON.parse(mfaSessionJson);
        
        // Odtwórz MfaSessionEntity z zapisanych danych
        const mfaSession = new MfaSessionEntity(
          data.tempToken,
          data.userId || 0,
          data.mfaMethod || 'email'
        );
        
        // Przywróć oryginalny czas wygaśnięcia i próby
        if (data.expiresAt) {
          mfaSession.expiresAt = new Date(data.expiresAt);
        }
        if (typeof data.attemptsLeft === 'number') {
          mfaSession.attemptsLeft = data.attemptsLeft;
        }
        
        // Sprawdź czy sesja jest wciąż ważna
        if (mfaSession.isValid) {
          this._mfaSession$.next(mfaSession);
          this.mfaRequired$.next(true);
        } else {
          this.clearMfaSession();
        }
      } catch (error) {
        console.error('Failed to initialize MFA session:', error);
        this.clearMfaSession();
      }
    }
  }
}
